import { Box, Button, Typography } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateRuleRulesPostMutation } from "api";
import { SelectInput, SwitchInput, TextInput } from "components/forms";
import { IRuleMovement } from "interfaces/rules.interface";

import { RuleMovement as config } from "../common/RulePresetMinMaxValues";
import { configurationSchema, timeRestrictionSchema } from "../common/schemas";
import { TimeRestrictionInput } from "../components/TimeRestrictionInput";

const conditionsSchema = z
  .object({
    initial_presence_time_epochs_gte: z.coerce
      .number()
      .min(config.initial_presence_time_epochs_gte.exclusiveMinimum)
      .max(config.initial_presence_time_epochs_gte.exclusiveMaximum)
      .default(config.initial_presence_time_epochs_gte.default),
    movement_mean_time_epochs_gte: z.coerce
      .number()
      .min(config.initial_presence_time_epochs_gte.exclusiveMinimum)
      .max(config.initial_presence_time_epochs_gte.exclusiveMaximum)
      .default(config.initial_presence_time_epochs_gte.default),
    movement_mean_gte: z.coerce
      .number()
      .min(config.initial_presence_time_epochs_gte.exclusiveMinimum)
      .max(config.initial_presence_time_epochs_gte.exclusiveMaximum)
      .default(config.initial_presence_time_epochs_gte.default),
  })
  .nullable()
  .default(null);

const ruleMovementSchema = z.object({
  time_restriction: timeRestrictionSchema,
  conditions_preset: z.enum(["default", ""]).nullable().default("default"),
  conditions: conditionsSchema,
  configuration: configurationSchema,
});

type RuleMovementSchema = z.infer<typeof ruleMovementSchema>;

type RuleMovementFormProps = {
  entityiD: string;
  presets: { id: string; name: string }[];
  defaultValues: IRuleMovement | null;
  disabled: boolean;
  onClose: () => void;
};

export function RuleMovementForm({
  entityiD,
  presets,
  defaultValues,
  disabled,
  onClose,
}: RuleMovementFormProps) {
  const { t } = useTranslation();

  const [createRule] = useCreateRuleRulesPostMutation();

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: ruleMovementSchema.safeParse(defaultValues ?? {}).data,
    resolver: zodResolver(ruleMovementSchema),
  });

  const onSubmit = async (data: RuleMovementSchema) => {
    await createRule({
      ruleCreateModels: {
        ...data,
        type: "subject.session.epochs.movement",
        entity_id: entityiD,
        description: null,
        enabled: true,
        conditions_preset: data.conditions_preset || null,
      },
    });

    onClose();
  };

  const presetId = watch("conditions_preset");

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit(onSubmit, console.error)}
      display={"flex"}
      flexDirection={"column"}
      flexGrow={1}
      gap={1}
    >
      <SelectInput
        control={control}
        label={t("preset")}
        fieldName={"conditions_preset"}
        options={presets}
        placeholder={t("custom")}
        optionLabelField={"name"}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.value.length === 0) {
            const value = conditionsSchema.parse({
              ok_to_alert: {},
              alert_to_ok: {},
            });

            setValue("conditions", value);
          } else {
            setValue("conditions", null);
          }
        }}
      />
      {!presetId && (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={1}
          padding={1}
          border={1}
          borderRadius={1}
          borderColor={"divider"}
        >
          <Typography variant={"bodyLargeMedium"}>
            {t("send_notification_message")}
          </Typography>
          <Box padding={1} display={"flex"} flexDirection={"column"} gap={1}>
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.initial_presence_time_epochs_gte"}
              label={t("minimum_time_presence_is")}
              type={"number"}
              unit={t("mins")}
              min={config.initial_presence_time_epochs_gte.exclusiveMinimum}
              max={config.initial_presence_time_epochs_gte.exclusiveMaximum}
            />
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.movement_mean_time_epochs_gte"}
              label={t("and") + " " + t("average_movement_in_the_last")}
              type={"number"}
              unit={t("mins")}
              min={config.movement_mean_time_epochs_gte.exclusiveMinimum}
              max={config.movement_mean_time_epochs_gte.exclusiveMaximum}
            />
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.movement_mean_gte"}
              label={t("is_greater_than_or_equal_to")}
              type={"number"}
              min={config.movement_mean_gte.exclusiveMinimum}
              max={config.movement_mean_gte.exclusiveMaximum}
            />
          </Box>
          <Typography variant={"bodyLargeMedium"}>
            {t("send_ok_message_only_through_SMS")}
          </Typography>
          <Box padding={1} display={"flex"} flexDirection={"column"} gap={1}>
            <SwitchInput
              control={control}
              disabled={disabled}
              fieldName={"configuration.execute_actions_on_alert_to_ok"}
              label={t("Enabled")}
            />
          </Box>
        </Box>
      )}
      <TimeRestrictionInput
        control={control}
        setValue={setValue}
        disabled={disabled}
      />
      <Box flexGrow={1} />
      {!disabled && <Button type={"submit"}>{t("Create")}</Button>}
    </Box>
  );
}
